import { useState, useEffect, useMemo } from "react";
import {
  Loader2, AlertCircle, AlertTriangle, Activity as ActivityIcon,
  Users, Calendar, ChevronLeft, ChevronRight, ArrowRight, TrendingUp,
} from "lucide-react";
import { Badge } from "../ui/badge";
import WorkflowService from "../../services/workflow.service";
import { useOrganizations } from "../../hooks/useOrganizations";
import ActivityHeatmap, { isoDay } from "../ui/ActivityHeatmap";
import MobileMenuButton from "../Sidebar/MobileMenuButton";
import { useHeaderSlot } from "../Sidebar/MobileNavContext";
import { useIsMobile } from "../../hooks/useMediaQuery";
import AvatarStack from "../ui/AvatarStack";
import SectionTabs from "../ui/SectionTabs";

// ============================================================================
// SHARED HELPERS
// ============================================================================

const DAY_MS = 86400000;
const startOfDay = (d) => { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; };

const RUNNING_STATES = new Set(["running", "pending"]);
const WAITING_STATES = new Set(["paused", "awaiting_input", "waiting_for_webhook", "waiting"]);
const FAILED_STATES = new Set(["failed", "expired", "timeout"]);
const ATTENTION_STATES = new Set([...WAITING_STATES, ...FAILED_STATES]);

const STATUS_META = {
  running: { label: "Running", dot: "bg-blue-500", text: "text-blue-400" },
  pending: { label: "Queued", dot: "bg-sky-500", text: "text-sky-400" },
  paused: { label: "Paused", dot: "bg-amber-500", text: "text-amber-400" },
  awaiting_input: { label: "Awaiting input", dot: "bg-amber-500", text: "text-amber-400" },
  waiting_for_webhook: { label: "Waiting", dot: "bg-amber-500", text: "text-amber-400" },
  waiting: { label: "Waiting", dot: "bg-amber-500", text: "text-amber-400" },
  completed: { label: "Completed", dot: "bg-emerald-500", text: "text-emerald-400" },
  failed: { label: "Failed", dot: "bg-red-500", text: "text-red-400" },
  expired: { label: "Expired", dot: "bg-red-500", text: "text-red-400" },
  timeout: { label: "Timed out", dot: "bg-orange-500", text: "text-orange-400" },
  cancelled: { label: "Cancelled", dot: "bg-gray-500", text: "text-gray-400" },
};
const statusMeta = (s) => STATUS_META[s] || { label: s || "Unknown", dot: "bg-gray-500", text: "text-gray-400" };

const safeName = (v) => {
  if (!v) return "Workflow";
  if (typeof v === "string") return v;
  if (typeof v === "object" && v.name) return v.name;
  return String(v);
};

const relTime = (iso) => {
  if (!iso) return "";
  const diff = Date.now() - new Date(iso).getTime();
  if (Number.isNaN(diff)) return "";
  const s = Math.floor(diff / 1000);
  if (s < 45) return "just now";
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d ago`;
  const w = Math.floor(d / 7);
  if (w < 5) return `${w}w ago`;
  return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric" });
};

const TRIGGER_BADGES = {
  shopify: { label: "Shopify", className: "bg-emerald-500/15 text-emerald-400 border-emerald-500/40" },
  webhook: { label: "Webhook", className: "bg-orange-500/15 text-orange-400 border-orange-500/40" },
  schedule: { label: "Schedule", className: "bg-blue-500/15 text-blue-400 border-blue-500/40" },
  scheduled: { label: "Schedule", className: "bg-blue-500/15 text-blue-400 border-blue-500/40" },
  whatsapp: { label: "WhatsApp", className: "bg-teal-500/15 text-teal-400 border-teal-500/40" },
  api_call: { label: "API", className: "bg-cyan-500/15 text-cyan-400 border-cyan-500/40" },
  manual: { label: "Manual", className: "bg-gray-500/15 text-gray-400 border-gray-500/40" },
};
const TriggerBadge = ({ type }) => {
  const b = TRIGGER_BADGES[type] || TRIGGER_BADGES.manual;
  return (
    <Badge variant="outline" className={`hidden sm:inline-flex text-[10px] font-semibold border backdrop-blur-sm ${b.className}`}>
      {b.label}
    </Badge>
  );
};

const execId = (e) => e.execution_id || e.id;

// ---- small shared UI bits ----

const CenterLoader = () => (
  <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-violet-500" /></div>
);
const ErrorPanel = ({ msg }) => (
  <div className="flex flex-col items-center justify-center py-16 bg-black/40 border border-white/10 rounded-2xl">
    <div className="flex items-center gap-3 text-red-400 mb-2"><AlertCircle className="w-5 h-5" /><span className="text-sm font-medium">Something went wrong</span></div>
    <p className="text-xs text-gray-500">{msg}</p>
  </div>
);
const EmptyState = ({ label }) => (
  <div className="text-center py-12 text-sm text-gray-600 bg-black/20 border border-white/5 rounded-2xl">{label}</div>
);
const SectionLabel = ({ children }) => (
  <h4 className="text-[11px] font-bold uppercase tracking-wider text-gray-500 mb-2">{children}</h4>
);

const RunRow = ({ exec, onOpenRun }) => {
  const meta = statusMeta(exec.status);
  const clickable = typeof onOpenRun === "function";
  return (
    <button
      type="button"
      disabled={!clickable}
      onClick={() => clickable && onOpenRun(execId(exec))}
      className={`w-full flex items-center gap-2 md:gap-3 px-3 py-2.5 rounded-xl border border-white/5 bg-black/20 text-left transition-all ${
        clickable ? "hover:border-violet-500/30 hover:bg-white/[0.03] cursor-pointer" : "cursor-default"
      }`}
    >
      {/* Los anchos fijos de estado y hora son para que las filas se alineen en
          columna. En un teléfono esos 176px son la mitad del ancho útil y el
          nombre —lo único por lo que se reconoce una corrida— se queda en tres
          letras. Ahí cada uno ocupa lo suyo y el nombre se lleva el resto. */}
      <span className={`w-2 h-2 rounded-full shrink-0 ${meta.dot} ${RUNNING_STATES.has(exec.status) ? "animate-pulse" : ""}`} />
      <span className="text-sm font-medium text-white/90 truncate flex-1 min-w-0">{safeName(exec.workflow_name)}</span>
      <TriggerBadge type={safeName(exec.trigger_type || "manual")} />
      <span className={`text-xs font-semibold ${meta.text} text-right shrink-0 md:w-28`}>{meta.label}</span>
      <span className="text-[11px] text-gray-500 text-right shrink-0 md:w-16">{relTime(exec.started_at || exec.created_at)}</span>
    </button>
  );
};

const SeeMore = ({ count, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-violet-400 hover:text-violet-300 transition-colors"
  >
    See {count} more {count === 1 ? "run" : "runs"} <ArrowRight className="w-3.5 h-3.5" />
  </button>
);

// ============================================================================
// OVERVIEW
// ============================================================================

const FEED_CAP = 5;
const TIER_LABEL = { today: "Today", week: "This week", month: "This month" };

// Calendar-based, mutually-exclusive recency tiers (most recent first):
// today; the current calendar week (Mon→today) minus today; the current
// calendar month (1st→today) minus this week. Assigning with the first match
// keeps them exclusive, so "This month" never repeats "This week".
function calendarTiers() {
  const t0 = startOfDay(new Date());
  const dow = t0.getDay(); // 0=Sun..6=Sat
  const weekStart = new Date(t0);
  weekStart.setDate(t0.getDate() - (dow === 0 ? 6 : dow - 1)); // back to Monday
  const monthStart = new Date(t0.getFullYear(), t0.getMonth(), 1);
  return { t0, weekStart, monthStart };
}
function tierOf(date, b) {
  const d = startOfDay(date);
  if (Number.isNaN(d.getTime())) return null;
  if (d.getTime() === b.t0.getTime()) return "today";
  if (d >= b.weekStart) return "week";
  if (d >= b.monthStart) return "month";
  return null;
}

// Older, non-detailed tiers collapse to this one-liner instead of a wall of rows.
function SummaryChip({ label, count, rate, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full flex items-center gap-2 px-4 py-3 rounded-xl border border-white/5 bg-black/20 text-left hover:border-violet-500/30 hover:bg-white/[0.03] transition-all"
    >
      <span className="text-sm font-semibold text-white/80">{label}</span>
      <span className="text-gray-600">·</span>
      <span className="text-sm text-gray-400">{count} {count === 1 ? "run" : "runs"}</span>
      {rate != null && (
        <>
          <span className="text-gray-600">·</span>
          <span className="text-sm text-gray-400">{rate}% success</span>
        </>
      )}
      <ArrowRight className="w-4 h-4 text-violet-400 ml-auto" />
    </button>
  );
}

function OverviewView({ projectId, accountId, onOpenRun, onViewRuns, onOpenWorkflow }) {
  const isMobile = useIsMobile();
  const [buckets, setBuckets] = useState([]);
  const [activeDays, setActiveDays] = useState(0);
  const [runs, setRuns] = useState([]);
  const [agentsById, setAgentsById] = useState({});
  const [wfAgents, setWfAgents] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!projectId || !accountId) return;
    let alive = true;
    (async () => {
      setLoading(true);
      setError(null);
      const [act, ex, ags] = await Promise.all([
        WorkflowService.getWorkflowActivity({ account_id: accountId, project_id: projectId }),
        WorkflowService.listExecutions({ account_id: accountId, project_id: projectId, limit: 100, sort_by: "started_at", sort_order: "desc" }),
        WorkflowService.getProjectAgents({ account_id: accountId, project_id: projectId }),
      ]);
      if (!alive) return;
      if (act.success) {
        setBuckets(act.data.buckets || []);
        setActiveDays(act.data.active_days || 0);
      }
      if (ex.success) setRuns(ex.data.executions || []);
      else setError(ex.error || "Failed to load activity");
      if (ags.success) {
        const m = {};
        (ags.data.agents || []).forEach((a) => { m[a.id] = a; });
        setAgentsById(m);
      }
      setLoading(false);
    })();
    return () => { alive = false; };
  }, [projectId, accountId]);

  const heatCounts = useMemo(() => {
    const m = {};
    buckets.forEach((b) => { if (b.date) m[b.date] = b.count; });
    return m;
  }, [buckets]);

  // TRUE per-tier totals from the server day-buckets (count/succeeded/failed),
  // so "See N more" and the collapsed summaries reflect the real period — not
  // the recent-100 sample the individual rows are drawn from.
  const tierTotals = useMemo(() => {
    const b = calendarTiers();
    const acc = {
      today: { count: 0, succ: 0, fail: 0 },
      week: { count: 0, succ: 0, fail: 0 },
      month: { count: 0, succ: 0, fail: 0 },
    };
    buckets.forEach((bk) => {
      if (!bk.date) return;
      const tier = tierOf(new Date(`${bk.date}T00:00:00`), b);
      if (!tier) return;
      acc[tier].count += bk.count || 0;
      acc[tier].succ += bk.succeeded || 0;
      acc[tier].fail += bk.failed || 0;
    });
    return acc;
  }, [buckets]);

  // Individual rows (for the detailed/freshest tier) + needs-attention + top
  // workflows, all derived from the recent runs sample.
  const { runsByTier, attention, topWorkflows } = useMemo(() => {
    const b = calendarTiers();
    const runsByTier = { today: [], week: [], month: [] };
    const attention = [];
    const wfCount = {};

    runs.forEach((e) => {
      if (ATTENTION_STATES.has(e.status)) attention.push(e);
      const tier = tierOf(new Date(e.started_at || e.created_at), b);
      if (!tier) return;
      // Running runs are transient — the feed is a log of what happened.
      if (!RUNNING_STATES.has(e.status)) runsByTier[tier].push(e);
      if (e.workflow_id) {
        if (!wfCount[e.workflow_id]) wfCount[e.workflow_id] = { id: e.workflow_id, name: safeName(e.workflow_name), count: 0 };
        wfCount[e.workflow_id].count += 1;
      }
    });

    const topWorkflows = Object.values(wfCount).sort((a, c) => c.count - a.count).slice(0, 3);
    return { runsByTier, attention, topWorkflows };
  }, [runs]);

  // Resolve the agents participating in each top workflow (Epic 32 index).
  useEffect(() => {
    if (!topWorkflows.length) { setWfAgents({}); return; }
    let alive = true;
    (async () => {
      const entries = await Promise.all(topWorkflows.map(async (w) => {
        const res = await WorkflowService.listWorkflowAgents(w.id, { source: "published" });
        const ids = res.success ? res.data.agent_ids : [];
        const agents = ids.map((id) => agentsById[id] || { id, name: "Agent" });
        return [w.id, agents];
      }));
      if (alive) setWfAgents(Object.fromEntries(entries));
    })();
    return () => { alive = false; };
  }, [topWorkflows, agentsById]);

  if (loading) return <CenterLoader />;
  if (error) return <ErrorPanel msg={error} />;

  const maxTop = Math.max(1, ...topWorkflows.map((w) => w.count));

  return (
    <div className="space-y-8">
      {/* 1. Heatmap */}
      <section>
        <div className="flex items-center gap-2 mb-3">
          <ActivityIcon className="w-4 h-4 text-violet-400" />
          <h3 className="text-sm font-bold text-white">Runs this year</h3>
          <span className="text-xs text-gray-500">· {activeDays} active {activeDays === 1 ? "day" : "days"}</span>
        </div>
        <div className="rounded-2xl border border-white/10 bg-black/30 p-4 overflow-x-auto">
          <ActivityHeatmap counts={heatCounts} weeks={53} legend size={isMobile ? "xs" : "md"} />
        </div>
      </section>

      {/* 2. Top workflows */}
      {topWorkflows.length > 0 && (
        <section>
          <div className="flex items-center gap-2 mb-3">
            <TrendingUp className="w-4 h-4 text-violet-400" />
            <h3 className="text-sm font-bold text-white">Top workflows</h3>
            <span className="text-xs text-gray-500">· this month</span>
          </div>
          <div className="rounded-2xl border border-white/10 bg-black/30 divide-y divide-white/5">
            {topWorkflows.map((w, i) => (
              <button
                key={w.id}
                type="button"
                onClick={() => onOpenWorkflow?.(w)}
                className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-white/[0.03] transition-colors first:rounded-t-2xl last:rounded-b-2xl sm:gap-4"
              >
                <span className="text-xs font-bold text-gray-600 w-4 shrink-0">{i + 1}</span>
                <span className="text-sm font-medium text-white/90 truncate flex-1 min-w-0">{w.name}</span>
                {/* Los avatares se caen en móvil: entre el nombre, la barra y
                    la cuenta no queda sitio, y de las tres cosas son la que
                    menos dice sobre "cuál se ejecutó más". */}
                <div className="hidden sm:block">
                  <AvatarStack items={wfAgents[w.id] || []} max={3} size="sm" ring="ring-[#0a0a0b]" />
                </div>
                <div className="flex items-center gap-2 w-28 shrink-0 sm:w-40">
                  <div className="flex-1 h-1.5 rounded-full bg-white/5 overflow-hidden">
                    <div className="h-full rounded-full bg-gradient-to-r from-violet-500 to-purple-500" style={{ width: `${(w.count / maxTop) * 100}%` }} />
                  </div>
                  <span className="text-xs font-semibold text-gray-400 w-10 text-right">{w.count}</span>
                </div>
              </button>
            ))}
          </div>
        </section>
      )}

      {/* 3. Feed — freshest tier with runs shows rows; older tiers collapse to a summary */}
      {(() => {
        const order = ["today", "week", "month"];
        const nonEmpty = order.filter((t) => tierTotals[t].count > 0);
        if (nonEmpty.length === 0) return <EmptyState label="No activity this month" />;

        const detailed = nonEmpty[0];
        const rows = runsByTier[detailed] || [];
        const shown = Math.min(FEED_CAP, rows.length);
        const more = tierTotals[detailed].count - shown;

        return (
          <div className="space-y-3">
            <section>
              <SectionLabel>{TIER_LABEL[detailed]}</SectionLabel>
              <div className="space-y-2">
                {rows.slice(0, FEED_CAP).map((e) => <RunRow key={execId(e)} exec={e} onOpenRun={onOpenRun} />)}
              </div>
              {more > 0 && <SeeMore count={more} onClick={onViewRuns} />}
            </section>
            {nonEmpty.slice(1).map((t) => {
              const { count, succ, fail } = tierTotals[t];
              const finished = succ + fail;
              const rate = finished > 0 ? Math.round((succ / finished) * 100) : null;
              return <SummaryChip key={t} label={TIER_LABEL[t]} count={count} rate={rate} onClick={onViewRuns} />;
            })}
          </div>
        );
      })()}

      {/* 4. Needs attention */}
      {attention.length > 0 && (
        <section className="rounded-2xl border border-amber-500/25 bg-amber-500/[0.04] p-4">
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle className="w-4 h-4 text-amber-400" />
            <h3 className="text-sm font-bold text-amber-300">Needs attention</h3>
            <span className="text-xs font-semibold text-amber-400/80 bg-amber-500/10 px-2 py-0.5 rounded-full">{attention.length}</span>
          </div>
          <div className="space-y-2">
            {attention.map((e) => <RunRow key={execId(e)} exec={e} onOpenRun={onOpenRun} />)}
          </div>
        </section>
      )}
    </div>
  );
}

// ============================================================================
// BY AGENT
// ============================================================================

function AgentActivityCard({ agent, accountId, projectId }) {
  const [runs, setRuns] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [avatarError, setAvatarError] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      const res = await WorkflowService.listExecutions({
        account_id: accountId, project_id: projectId, agent_id: agent.id,
        limit: 100, sort_by: "started_at", sort_order: "desc",
      });
      if (!alive) return;
      if (res.success) { setRuns(res.data.executions || []); setStats(res.data.statistics || {}); }
      setLoading(false);
    })();
    return () => { alive = false; };
  }, [agent.id, accountId, projectId]);

  const counts = useMemo(() => {
    const map = {};
    runs.forEach((r) => { const k = isoDay(r.started_at || r.created_at); if (k) map[k] = (map[k] || 0) + 1; });
    return map;
  }, [runs]);

  const totalRuns = stats?.total_count ?? runs.length;
  const successRate = typeof stats?.success_rate === "number" ? stats.success_rate : null;
  const lastRun = runs[0]?.started_at || runs[0]?.created_at;
  const initial = (agent.name || "?").trim().charAt(0).toUpperCase();
  const successColor = successRate == null ? "text-gray-500"
    : successRate >= 80 ? "text-emerald-400"
    : successRate < 50 ? "text-red-400" : "text-white";

  return (
    <div className="rounded-2xl border border-white/10 bg-black/30 p-4 hover:border-violet-500/30 transition-all">
      <div className="flex items-center gap-3 mb-4">
        {agent.avatar && !avatarError ? (
          <img
            src={agent.avatar}
            alt={agent.name || "Agent"}
            onError={() => setAvatarError(true)}
            className="w-9 h-9 rounded-xl object-cover border border-violet-500/30 shrink-0"
          />
        ) : (
          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-violet-600/30 to-purple-600/30 border border-violet-500/30 flex items-center justify-center text-sm font-bold text-violet-200 shrink-0">
            {initial}
          </div>
        )}
        <div className="min-w-0">
          <h4 className="text-sm font-semibold text-white truncate">{agent.name || "Agent"}</h4>
          <p className="text-[11px] text-gray-500">{lastRun ? `Last run ${relTime(lastRun)}` : "No runs yet"}</p>
        </div>
      </div>

      {loading ? (
        <div className="h-[52px] flex items-center"><Loader2 className="w-4 h-4 animate-spin text-violet-500" /></div>
      ) : (
        <>
          <div className="overflow-x-auto">
            <ActivityHeatmap counts={counts} weeks={13} size="sm" months={false} />
          </div>
          <div className="grid grid-cols-2 gap-3 mt-4">
            <div className="rounded-xl bg-white/[0.03] border border-white/5 px-3 py-2">
              <div className="text-[10px] font-semibold uppercase tracking-wider text-gray-500">Runs</div>
              <div className="text-lg font-bold text-white">{totalRuns}</div>
            </div>
            <div className="rounded-xl bg-white/[0.03] border border-white/5 px-3 py-2">
              <div className="text-[10px] font-semibold uppercase tracking-wider text-gray-500">Success</div>
              <div className={`text-lg font-bold ${successColor}`}>{successRate == null ? "—" : `${Math.round(successRate)}%`}</div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function ByAgentView({ projectId, accountId }) {
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!projectId || !accountId) return;
    let alive = true;
    (async () => {
      setLoading(true);
      const res = await WorkflowService.getProjectAgents({ account_id: accountId, project_id: projectId });
      if (!alive) return;
      if (res.success) { setAgents(res.data.agents || []); setError(null); }
      else setError(res.error || "Failed to load agents");
      setLoading(false);
    })();
    return () => { alive = false; };
  }, [projectId, accountId]);

  if (loading) return <CenterLoader />;
  if (error) return <ErrorPanel msg={error} />;
  if (!agents.length) return <EmptyState label="No agents in this project yet" />;

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
      {agents.map((a) => <AgentActivityCard key={a.id} agent={a} accountId={accountId} projectId={projectId} />)}
    </div>
  );
}

// ============================================================================
// CALENDAR
// ============================================================================


const CAL_STATUS_STYLE = {
  completed: { card: "bg-emerald-500/15 border-emerald-500/35", time: "text-emerald-400", name: "text-white/80" },
  failed: { card: "bg-red-500/15 border-red-500/35", time: "text-red-400", name: "text-white/80" },
  running: { card: "bg-blue-500/15 border-blue-500/35", time: "text-blue-400", name: "text-white/80" },
  cancelled: { card: "bg-gray-500/10 border-gray-500/25", time: "text-gray-400", name: "text-gray-400" },
  timeout: { card: "bg-orange-500/15 border-orange-500/35", time: "text-orange-400", name: "text-white/80" },
};

function CalEventCard({ time, name, status, isFuture }) {
  if (isFuture) {
    return (
      <div className="rounded-lg px-2.5 py-2 mb-1.5 border border-dashed border-white/12 bg-transparent text-left w-full">
        <div className="text-[10px] font-semibold text-gray-600 leading-none mb-1">{time}</div>
        <div className="text-[11px] font-medium text-gray-600 truncate leading-tight">{name}</div>
      </div>
    );
  }
  const style = CAL_STATUS_STYLE[status] || CAL_STATUS_STYLE.completed;
  return (
    <div className={`rounded-lg px-2.5 py-2 mb-1.5 border ${style.card} text-left w-full`}>
      <div className={`text-[10px] font-semibold ${style.time} leading-none mb-1`}>{time}</div>
      <div className={`text-[11px] font-medium ${style.name} truncate leading-tight`}>{name}</div>
    </div>
  );
}

const STATUS_DOT = {
  completed: "bg-emerald-500", failed: "bg-red-500", expired: "bg-red-500",
  timeout: "bg-orange-500", running: "bg-blue-500", pending: "bg-sky-500",
  cancelled: "bg-gray-500", paused: "bg-amber-500",
  awaiting_input: "bg-amber-500", waiting_for_webhook: "bg-amber-500",
};
const statusDot = (s) => STATUS_DOT[s] || "bg-gray-500";

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];
const DOW_SHORT = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

// Monday on/before the given date.
const mondayOf = (date) => {
  const x = startOfDay(date);
  const dow = x.getDay();
  x.setDate(x.getDate() - (dow === 0 ? 6 : dow - 1));
  return x;
};

// Month grid: 6 rows × 7 days (Mon-first), covering the whole month plus the
// adjacent-month days that fill the first/last weeks.
function monthGridCells(monthCursor) {
  const first = new Date(monthCursor.getFullYear(), monthCursor.getMonth(), 1);
  const start = mondayOf(first);
  return Array.from({ length: 42 }, (_, i) => {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    d.setHours(0, 0, 0, 0);
    return d;
  });
}

function MonthCalendar({ projectId, onDayClick, monthCursor }) {
  const { currentAccount } = useOrganizations();
  const accountId = currentAccount?.id;
  const [countByDay, setCountByDay] = useState({});
  const [samplesByDay, setSamplesByDay] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const cells = useMemo(() => monthGridCells(monthCursor), [monthCursor]);

  useEffect(() => {
    if (!projectId || !accountId) return;
    let alive = true;
    (async () => {
      setLoading(true);
      setError(null);
      const since = cells[0];
      const until = new Date(cells[41].getTime() + DAY_MS); // exclusive end
      // One server aggregate: accurate per-day counts + up to 3 named runs per day
      // (top-N by recency, server-side). Every day with runs gets its names — not
      // just the recent few a bounded run-list would have reached.
      const act = await WorkflowService.getWorkflowActivity({
        account_id: accountId, project_id: projectId,
        since: since.toISOString(), until: until.toISOString(),
        include_samples: true, samples_per_day: 3,
      });
      if (!alive) return;
      if (act.success) {
        const counts = {};
        const samples = {};
        (act.data.buckets || []).forEach((b) => {
          if (!b.date) return;
          counts[b.date] = b;
          if (b.samples && b.samples.length) samples[b.date] = b.samples;
        });
        setCountByDay(counts);
        setSamplesByDay(samples);
      } else {
        setError(act.error || "Failed to load calendar");
      }
      setLoading(false);
    })();
    return () => { alive = false; };
  }, [projectId, accountId, cells]);

  const today = startOfDay(new Date());

  if (error) return <ErrorPanel msg={error} />;

  return (
    <div className="rounded-2xl border border-white/10 bg-black/40 overflow-hidden relative">
      {loading && <div className="absolute top-2 right-2 z-10"><Loader2 className="w-4 h-4 animate-spin text-gray-500" /></div>}
      {/* Siete columnas en 390px salen a 55px cada una: los 12px de aire
          lateral que se ven bien en un monitor aquí son un cuarto de la
          columna. */}
      <div className="grid grid-cols-7 border-b border-white/10">
        {DOW_SHORT.map((d) => (
          <div key={d} className="px-1 py-2 text-center text-[10px] font-semibold uppercase tracking-wider text-gray-500 md:px-3 md:text-left md:text-[11px]">{d}</div>
        ))}
      </div>
      <div className="grid grid-cols-7">
        {cells.map((d, i) => {
          const iso = isoDay(d);
          const inMonth = d.getMonth() === monthCursor.getMonth();
          const isToday = d.getTime() === today.getTime();
          const total = countByDay[iso]?.count || 0;
          const failed = countByDay[iso]?.failed || 0;
          const samples = (samplesByDay[iso] || []).slice(0, 3);
          const more = total - samples.length;
          return (
            <button
              key={i}
              type="button"
              onClick={() => onDayClick(d)}
              className={`text-left border-r border-b last:border-r-0 border-white/5 min-h-[56px] p-1.5 align-top transition-colors md:min-h-[112px] md:p-2 ${
                isToday ? "bg-violet-500/[0.06]" : inMonth ? "hover:bg-white/[0.03]" : "bg-black/20"
              }`}
            >
              <div className="mb-1 flex items-baseline justify-between gap-1">
                <span className={`text-xs font-bold ${isToday ? "text-violet-300" : inMonth ? "text-gray-300" : "text-gray-700"}`}>{d.getDate()}</span>
                {/* El total va al lado del día sólo cuando hay sitio. En móvil
                    baja a su propia línea: "27" y "158" juntos piden 41px y la
                    celda da 35. */}
                {total > 0 && (
                  <span className="hidden text-[10px] font-semibold text-gray-500 md:inline">{total}</span>
                )}
              </div>

              {/* En móvil la celda no lleva nombres, y no es una simplificación
                  estética: una semana son siete columnas, así que el ancho de
                  una celda está fijado en ~50px pase lo que pase. Medido a
                  360/390/430px, del nombre de un workflow se llegan a leer
                  entre 3 y 6 caracteres —"Sun", "Sunc", "Sunchi"—, y como los
                  workflows de un proyecto suelen compartir prefijo, todos los
                  días del mes acabarían diciendo lo mismo.
                  Lo que sí cabe y sí distingue un día de otro son dos cosas:
                  cuántas corridas hubo y si alguna falló. Van en el mismo
                  número —el color lo dice— porque dos elementos no caben.
                  Los nombres no se pierden: tocar el día abre la vista de Day,
                  que es una sola columna y los lista enteros. */}
              {total > 0 && (
                <div
                  className={`text-[13px] font-semibold tabular-nums md:hidden ${
                    failed > 0 ? "text-rose-300" : "text-violet-400"
                  }`}
                  title={failed > 0 ? `${total} runs · ${failed} failed` : `${total} runs`}
                >
                  {total}
                </div>
              )}

              <div className="hidden space-y-1 md:block">
                {samples.map((e, si) => (
                  <div key={`${iso}-${si}`} className="flex items-center gap-1.5 rounded bg-white/[0.03] px-1 py-0.5">
                    <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${statusDot(e.status)}`} />
                    <span className="truncate text-[11px] text-white/80">{safeName(e.workflowName || e.workflow_name)}</span>
                  </div>
                ))}
                {more > 0 && <div className="px-1 text-[10px] font-semibold text-violet-400">+{more} more</div>}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function CalendarView({ projectId }) {
  const [mode, setMode] = useState("month");
  // Day view replaced the week grid: a single day is a bounded unit (one column,
  // never seven), so the 100-run cap almost always covers it, and when it doesn't
  // the "+N more" reads honestly. `daySeed` is the day the Day view opens on.
  const [daySeed, setDaySeed] = useState(() => startOfDay(new Date()));
  const [monthCursor, setMonthCursor] = useState(() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1);
  });

  const shiftMonth = (delta) => setMonthCursor((c) => new Date(c.getFullYear(), c.getMonth() + delta, 1));
  const thisMonth = () => setMonthCursor(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });

  const openDayOf = (date) => {
    setDaySeed(startOfDay(date));
    setMode("day");
  };

  const navBtn = "p-1.5 rounded-lg text-gray-400 hover:text-white hover:bg-white/5 border border-white/10 transition-all";
  // `flex-1` en móvil: la barra ocupa el ancho de la pantalla, así que los dos
  // botones se lo reparten en vez de amontonarse a la izquierda.
  const modeBtn = (active) => `flex-1 md:flex-none px-4 py-2 md:py-1.5 text-xs font-semibold rounded-lg transition-all ${
    active ? "bg-gradient-to-r from-violet-600 to-purple-600 text-white shadow-lg shadow-violet-500/20" : "text-gray-400 hover:text-white hover:bg-white/5"
  }`;

  return (
    <div>
      {/* Dos filas en móvil. Juntos, el mes con sus flechas, "Today" y el
          selector Month/Day piden ~430px y el más estrecho de los tres —el que
          cede— acaba siendo la etiqueta del mes, que es lo que dice dónde
          estás. El orden se invierte a propósito: el selector arriba, como en
          el diseño, y la navegación pegada a la rejilla que gobierna. */}
      <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        {mode === "month" ? (
          <div className="order-2 flex items-center gap-2 md:order-1">
            <button onClick={() => shiftMonth(-1)} className={navBtn}><ChevronLeft className="w-4 h-4" /></button>
            <span className="flex-1 text-center text-sm font-medium text-gray-300 md:min-w-[160px] md:flex-none">
              {MONTH_NAMES[monthCursor.getMonth()]} {monthCursor.getFullYear()}
            </span>
            <button onClick={() => shiftMonth(1)} className={navBtn}><ChevronRight className="w-4 h-4" /></button>
            <button onClick={thisMonth} className="shrink-0 whitespace-nowrap rounded-lg border border-violet-500/30 px-3 py-1.5 text-xs font-medium text-violet-400 transition-all hover:bg-violet-500/10">Today</button>
          </div>
        ) : (
          <div className="order-2 md:order-1" />
        )}
        <div className="order-1 flex gap-1 self-stretch rounded-xl border border-white/10 bg-black/40 p-1 backdrop-blur-xl md:order-2 md:self-auto">
          <button onClick={() => setMode("month")} className={modeBtn(mode === "month")}>Month</button>
          <button onClick={() => setMode("day")} className={modeBtn(mode === "day")}>Day</button>
        </div>
      </div>

      {mode === "month" ? (
        <MonthCalendar projectId={projectId} monthCursor={monthCursor} onDayClick={openDayOf} />
      ) : (
        <DayCalendar key={+daySeed} projectId={projectId} initialDate={daySeed} />
      )}
    </div>
  );
}

function DayCalendar({ projectId, initialDate }) {
  const { currentAccount } = useOrganizations();
  const accountId = currentAccount?.id;
  const [dayCursor, setDayCursor] = useState(() => startOfDay(initialDate || new Date()));
  const [runs, setRuns] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!projectId || !accountId) return;
    let alive = true;
    (async () => {
      setLoading(true);
      setError(null);
      const start = dayCursor;
      const end = new Date(dayCursor.getTime() + DAY_MS);
      const [ex, act] = await Promise.all([
        // Fetch desc (the runner reliably returns the window's most-recent runs that
        // way; asc came back empty on bounded fetches) and sort ascending client-side
        // for an agenda read. A day rarely exceeds 100, so nothing is usually hidden.
        WorkflowService.listExecutions({
          account_id: accountId, project_id: projectId,
          start_date: start.toISOString(), end_date: end.toISOString(),
          limit: 100, sort_by: "started_at", sort_order: "desc",
        }),
        // Accurate per-day total → an honest "+N more" when the 100 cap does bite.
        WorkflowService.getWorkflowActivity({
          account_id: accountId, project_id: projectId,
          since: start.toISOString(), until: end.toISOString(),
        }),
      ]);
      if (!alive) return;
      if (!ex.success) { setError(ex.error || "Failed to load day"); setLoading(false); return; }
      const list = ex.data.executions || [];
      setRuns(list);
      if (act.success) {
        const b = (act.data.buckets || []).find((x) => x.date === isoDay(dayCursor));
        setTotal(b ? b.count : list.length);
      } else {
        setTotal(list.length);
      }
      setLoading(false);
    })();
    return () => { alive = false; };
  }, [projectId, accountId, dayCursor]);

  const events = useMemo(() => {
    return (runs || [])
      .map((r) => {
        const d = new Date(r.started_at || r.created_at);
        return {
          time: d.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: false }),
          name: safeName(r.workflow_name),
          status: r.status,
          sortKey: d.getTime(),
        };
      })
      .sort((a, b) => a.sortKey - b.sortKey);
  }, [runs]);

  const more = Math.max(0, total - events.length);
  const isToday = dayCursor.getTime() === startOfDay(new Date()).getTime();
  const shiftDay = (delta) => setDayCursor((c) => startOfDay(new Date(c.getTime() + delta * DAY_MS)));
  // "Wednesday, Aug 27, 2026" pide 220px, y entre las dos flechas y "Today" no
  // quedan en un teléfono. El día de la semana se abrevia y el año se cae: la
  // navegación es de día en día, así que el año no está en duda.
  const label = dayCursor.toLocaleDateString("en-US", { weekday: "long", month: "short", day: "numeric", year: "numeric" });
  const shortLabel = dayCursor.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
  const navBtn = "p-1.5 rounded-lg text-gray-400 hover:text-white hover:bg-white/5 border border-white/10 transition-all shrink-0";

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div className="flex w-full items-center gap-2 md:w-auto">
          <button onClick={() => shiftDay(-1)} className={navBtn}><ChevronLeft className="w-4 h-4" /></button>
          <span className="flex-1 text-center text-sm font-medium text-gray-300 md:min-w-[220px] md:flex-none">
            <span className="md:hidden">{shortLabel}</span>
            <span className="hidden md:inline">{label}</span>
          </span>
          <button onClick={() => shiftDay(1)} className={navBtn}><ChevronRight className="w-4 h-4" /></button>
          {!isToday && (
            <button onClick={() => setDayCursor(startOfDay(new Date()))} className="shrink-0 whitespace-nowrap rounded-lg border border-violet-500/30 px-3 py-1.5 text-xs font-medium text-violet-400 transition-all hover:bg-violet-500/10">Today</button>
          )}
        </div>
      </div>

      {error ? (
        <div className="flex flex-col items-center justify-center py-16 bg-black/40 border border-white/10 rounded-2xl">
          <div className="flex items-center gap-3 text-red-400 mb-2"><AlertCircle className="w-5 h-5" /><span className="text-sm font-medium">Failed to load day</span></div>
          <p className="text-xs text-gray-500">{error}</p>
        </div>
      ) : loading ? (
        <div className="py-16 flex items-center justify-center gap-2 text-sm text-gray-500 bg-black/40 border border-white/10 rounded-2xl">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading runs…
        </div>
      ) : (
        <div className="bg-black/40 backdrop-blur-xl border border-white/10 rounded-2xl overflow-hidden">
          {events.length === 0 ? (
            <div className="py-16 flex items-center justify-center text-sm text-gray-600">No runs this day</div>
          ) : (
            <div className="max-h-[560px] overflow-y-auto p-3">
              {events.map((ev, i) => <CalEventCard key={i} time={ev.time} name={ev.name} status={ev.status} />)}
              {more > 0 && (
                <div className="pt-2 text-center text-xs font-semibold text-violet-400">
                  +{more} more this day · showing the latest 100
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// MAIN
// ============================================================================

const TABS = [
  { key: "overview", label: "Overview", Icon: ActivityIcon },
  { key: "by_agent", label: "By agent", Icon: Users },
  { key: "calendar", label: "Calendar", Icon: Calendar },
];

/**
 * @param extraTabs      Pestañas que añade quien lo monta (Activity le cuelga
 *                       "Usage"), para que quede una sola barra en vez de una
 *                       barra de sección con otra barra dentro.
 * @param renderExtraTab Qué pintar cuando una de esas está activa.
 * @param hideTitle      Lo usa quien ya nombra la sección por fuera.
 */
export default function ActivityBoard({
  projectId,
  onOpenRun,
  onViewRuns,
  onOpenWorkflow,
  extraTabs = [],
  renderExtraTab,
  hideTitle = false,
  emptyScopeMessage,
  // El control de alcance lo pinta cada pestana, no la seccion: Usage puede
  // responder por toda la cuenta y las otras tres no. Un unico selector arriba
  // obligaba a ofrecer "All projects" en pestanas que no lo soportan.
  scopeControl,
  // Modo controlado: quien lo monta puede tener la pestana en la URL, para que
  // recargar no vuelva a la primera y se pueda enlazar una concreta.
  tab: controlledTab,
  onTabChange,
}) {
  const { currentAccount } = useOrganizations();
  const accountId = currentAccount?.id;
  const [internalTab, setInternalTab] = useState("overview");
  const activeTab = controlledTab ?? internalTab;
  const isExtra = extraTabs.some((tab) => tab.key === activeTab);
  const isPrimaryHeader = useHeaderSlot(!hideTitle);
  const selectTab = (next) => {
    if (controlledTab == null) setInternalTab(next);
    onTabChange?.(next);
  };

  return (
    <div className="h-full">
      {/* Rejilla y no dos flex anidados por lo mismo que en `PageHeader`: la
          bajada va a lo ancho debajo de todo en móvil y sólo debajo del título
          en escritorio, y las pestañas tienen que poder ocupar la columna
          derecha entera sin renderizarse dos veces.
              móvil          ☰ | Activity | alcance
                             bajada, a lo ancho
                             pestañas, a lo ancho
              escritorio     Activity     |  pestañas
                             bajada       |
                             (el alcance va debajo, a la derecha) */}
      <div className="mb-6 grid grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-y-2 md:mb-8">
        {isPrimaryHeader ? (
          <MobileMenuButton className="col-start-1 row-start-1 mr-3" />
        ) : (
          <div className="col-start-1 row-start-1" />
        )}

        {hideTitle ? <div className="col-start-2 row-start-1" /> : (
          <h2 className="col-start-2 row-start-1 text-2xl font-bold text-white bg-gradient-to-r from-white to-gray-400 bg-clip-text text-transparent md:text-3xl">Activity</h2>
        )}

        {/* El alcance sube a la fila del título en móvil. Debajo, en su propia
            línea a la derecha, dejaba una franja casi vacía justo donde menos
            alto sobra. */}
        {!isExtra && scopeControl && (
          <div className="col-start-3 row-start-1 ml-3 min-w-0 max-w-[58%] md:hidden">{scopeControl}</div>
        )}

        {!hideTitle && (
          <p className="col-span-3 col-start-1 row-start-2 text-sm text-gray-500 max-w-2xl leading-relaxed md:col-span-1 md:col-start-2">
            What ran, what needs you, and what&apos;s coming up in this project.
          </p>
        )}

        <div className="col-span-3 col-start-1 row-start-3 mt-2 min-w-0 md:col-span-1 md:col-start-3 md:row-span-2 md:row-start-1 md:mt-0 md:justify-self-end">
          <SectionTabs
            ariaLabel="Activity"
            active={activeTab}
            onChange={selectTab}
            tabs={[...TABS, ...extraTabs]}
            // A lo ancho y con scroll en móvil: cuatro pestañas con icono no
            // entran en 360px, y una barra recortada esconde justo la última.
            className="flex w-full overflow-x-auto bg-black/40 backdrop-blur-xl md:inline-flex md:w-auto"
          />
        </div>
      </div>

      {/* Solo las pestanas del board: Usage trae el suyo, con periodo y con la
          opcion de toda la cuenta, que aqui no se puede ofrecer. */}
      {/* A la derecha, como el de Usage: el control es el mismo en las cuatro
          pestanas, asi que no puede saltar de lado al cambiar de una a otra.
          En móvil ya está arriba, en la fila del título. */}
      {!isExtra && scopeControl && (
        <div className="mb-5 hidden justify-end md:flex">{scopeControl}</div>
      )}

      {isExtra && renderExtraTab?.(activeTab)}

      {!isExtra && !projectId && emptyScopeMessage && (
        <div className="rounded-2xl border border-white/10 bg-white/[0.02] py-16 text-center text-sm text-gray-400">
          {emptyScopeMessage}
        </div>
      )}

      {!isExtra && projectId && activeTab === "overview" && (
        <OverviewView projectId={projectId} accountId={accountId} onOpenRun={onOpenRun} onViewRuns={onViewRuns} onOpenWorkflow={onOpenWorkflow} />
      )}
      {!isExtra && projectId && activeTab === "by_agent" && <ByAgentView projectId={projectId} accountId={accountId} />}
      {!isExtra && projectId && activeTab === "calendar" && <CalendarView projectId={projectId} />}
    </div>
  );
}
